import { Link, useParams } from 'react-router-dom';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import { NewsItem } from '../types/content';

function formatDate(value: string | undefined, uiLocale: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(uiLocale, { day: '2-digit', month: 'long', year: 'numeric' });
}

// Portal haber metnini duz metin olarak satir sonlariyla kaydediyor; her satir bir paragraf.
// Kayit "\n" metni olarak da gelebiliyor.
function contentParagraphs(value: string) {
  return value
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

// Kapak gorseli ve diger resimler belge rozeti olarak gosterilmez.
function newsDocuments(item: NewsItem) {
  const cover = mediaUrl(item.imageUrl);
  return (item.documents || []).filter((doc) => {
    const url = mediaUrl(doc.fileUrl);
    const isImage =
      Boolean(doc.icon?.startsWith('image/')) ||
      Boolean(doc.category?.startsWith('image/')) ||
      /\.(png|jpe?g|gif|webp|svg)$/i.test(doc.title || '');
    if (isImage) return false;
    if (cover && url && cover === url) return false;
    return true;
  });
}

export function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { content, isLoading } = useSiteContent();
  const { t, uiLocale } = useLanguage();
  const news = content.news;
  const item = news.find((n) => n.slug === slug);

  if (!item) {
    return (
      <section className="news-detail-section">
        <div className="container news-detail-empty">
          {isLoading ? (
            <p>{t('Yükleniyor...')}</p>
          ) : (
            <>
              <h1>{t('Haber bulunamadı')}</h1>
              <Link to="/haberler" className="news-detail-back">
                {t('← Tüm Haberler')}
              </Link>
            </>
          )}
        </div>
      </section>
    );
  }

  const cover = mediaUrl(item.imageUrl);
  const paragraphs = contentParagraphs(item.content || item.summary || '');
  const others = news.filter((n) => n.id !== item.id).slice(0, 3);

  return (
    <>
      <section className="news-detail-section">
        <div className="container news-detail-inner">
          <Link to="/haberler" className="news-detail-back">
            {t('← Tüm Haberler')}
          </Link>
          <div className="news-detail-meta">
            {item.category && <span className="news-detail-category">{t(item.category)}</span>}
            {item.publishedAt && <span>{formatDate(item.publishedAt, uiLocale)}</span>}
          </div>
          <h1 className="news-detail-title">{t(item.title)}</h1>
          {cover && <img className="news-detail-image" src={cover} alt={t(item.title)} />}
          <div className="news-detail-body">
            {paragraphs.map((paragraph, index) => (
              <p key={`p-${index}`}>{t(paragraph)}</p>
            ))}
          </div>
          <DocumentLinks documents={newsDocuments(item)} />
        </div>
      </section>

      {others.length > 0 && (
        <section className="news-detail-others">
          <div className="container">
            <h2 className="news-detail-others-title">{t('Diğer Haberler')}</h2>
            <div className="news-grid">
              {others.map((n) => (
                <div className="news-card" key={n.id}>
                  <div
                    className="img-fill"
                    style={n.imageUrl ? { backgroundImage: `url(${mediaUrl(n.imageUrl)})` } : undefined}
                  />
                  <div className="body">
                    {n.category && (
                      <div className="eyebrow-sm" style={{ marginBottom: 6 }}>
                        {t(n.category)}
                      </div>
                    )}
                    <h4>{t(n.title)}</h4>
                    {n.summary && <p>{t(n.summary)}</p>}
                    <Link to={`/haberler/${n.slug}`} className="readmore">
                      {t('Haberi Oku →')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
